import React, { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { ProjectCreateDrawerConsumer } from '@jira/project-create__drawer-context/src/index.tsx';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';
import { CloseButton } from '@atlaskit/modal-dialog/close-button';
import Button from '@atlaskit/button/new';
import Spinner from '@atlaskit/spinner/spinner';
import SectionMessage from '@atlaskit/section-message/message';
import { Box, Flex, Text, Pressable, Anchor, xcss } from '@atlaskit/primitives';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { KeyboardOrMouseEvent } from '@atlaskit/modal-dialog/types';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import { defineMessage, useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { CreateFromFilesIcon } from './CreateFromFilesIcon.tsx';
import { BringYourProjectIcon } from './BringYourProjectIcon.tsx';

const ROVO_FILE_UPLOAD_URL = '/gateway/api/assist/rovo/v1/chat/message/file';

// The instruction sent to Rovo alongside the uploaded file. This is a prompt for
// an LLM, not user-facing copy, so it is deliberately not translated: it is never
// rendered in the UI and Rovo replies in the user's own language regardless.
//
// It is scoped tightly on purpose. Keeping Rovo to a flat, 1:1 mapping of what
// is already in the file and suppressing its usual preamble keeps the hand-off
// fast and lands the user on created work items rather than a wall of
// explanation, which is what the import-on-nav experiment is measuring.
const ROVO_IMPORT_PROMPT = [
	'Read the uploaded file and generate a flat list of Jira work items from it. Preserve structured or tabular input as much as possible, and prefer 1:1 mapping when the file already contains distinct items. Keep titles clear, actionable, and verb-led. Keep descriptions brief (2 sentences maximum). Do not invent missing details, duplicates, owners, dependencies, or hierarchy.',
	'For each item include: summary, brief description, suggested issue type, and source reference if available.',
	'Do not explain your reasoning. Do not display the drafted items as a table. Create all items as Jira work items.',
].join('\n');

type RovoUploadedFile = {
	id: string;
	name: string;
	size?: number;
	type?: string;
};

type RovoFileUploadResponse = {
	data: RovoUploadedFile[];
};

// Mirrors STATUS_CODES from @atlassian/conversation-assistant-service-api/constants.
// 415 = the file extension/mime type itself isn't supported by the service.
// 406 = FILE_RAI_POLICY_VIOLATION — a Responsible AI / content-safety scan (run on
// the file's actual content, post-upload) flagged the file. This is NOT a file-type
// allow-list check, which is why the exact same file type can succeed once and be
// rejected another time depending on the specific file's content.
const UNSUPPORTED_FILE_TYPE_STATUS = 415;
const FILE_RAI_POLICY_VIOLATION_STATUS = 406;

class RovoFileUploadError extends Error {
	statusCode: number;

	constructor(statusCode: number, message: string) {
		super(message);
		this.statusCode = statusCode;
	}
}

const uploadFileToRovoAssistanceService = async (file: File): Promise<RovoUploadedFile[]> => {
	const formData = new FormData();
	formData.append('files', file);

	const response = await fetch(ROVO_FILE_UPLOAD_URL, {
		method: 'POST',
		headers: {
			'x-product': 'jira',
			'x-experience-id': 'ai-mate',
		},
		credentials: 'include',
		body: formData,
	});

	if (!response.ok) {
		throw new RovoFileUploadError(
			response.status,
			`Rovo file upload failed with status ${response.status}`,
		);
	}

	const json: RovoFileUploadResponse = await response.json();
	return json.data;
};

const titleMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.title',
	defaultMessage: 'Add work to your project',
	description: 'Title of the import choice modal',
});

const createFromFilesOptionMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.create-from-files-option',
	defaultMessage: 'Create work from files',
	description: 'Option to use Rovo AI to create issues from uploaded files',
});

const createFromFilesDescriptionMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.create-from-files-description',
	defaultMessage: 'Upload a csv, brief or photo. Rovo AI will draft tasks for you to review.',
	description: 'Description for the create from files option',
});

const bringYourProjectOptionMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.bring-your-project-option',
	defaultMessage: 'Migrate your project',
	description: 'Option to import a project from another tool',
});

const bringYourProjectDescriptionMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.bring-your-project-description',
	defaultMessage: 'Moving from another tool? We’ll bring over your work and structure.',
	description: 'Description for the bring your project option',
});

const mcpFooterTextMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.mcp-footer-text',
	defaultMessage: 'Working with AI agents?',
	description:
		'Standalone question opening the footer of the import choice modal. It is immediately followed by the separate sentence "Connect the Atlassian MCP so your agent can create and update work."',
});

const mcpFooterLinkMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.mcp-footer-link',
	defaultMessage:
		'<link>Connect the Atlassian MCP</link> so your agent can create and update work.',
	description:
		'Sentence in the footer of the import choice modal, following the "Working with AI agents?" question. The <link> tag wraps the words "Connect the Atlassian MCP" and links to the Atlassian MCP (Model Context Protocol) documentation.',
});

const cancelMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.cancel',
	defaultMessage: 'Cancel',
	description: 'Cancel button label',
});

const closeButtonLabelMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.close-button-label',
	defaultMessage: 'Close',
	description: 'Accessible label for the close button in the import choice modal',
});

const uploadingLabelMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.uploading-label',
	defaultMessage: 'Uploading your file…',
	description:
		'Label shown underneath a loading spinner on the create-from-files card while the selected file is being uploaded to Rovo. Replaces the card title and description for the duration of the upload.',
});

const uploadErrorGenericMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.upload-error-generic',
	defaultMessage: "We couldn't upload your file. Please try again.",
	description: 'Error banner shown when the Rovo file upload fails for an unknown reason',
});

const uploadErrorUnsupportedTypeMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.upload-error-unsupported-type',
	defaultMessage: "This file type isn't supported. Try a different file.",
	description: 'Error banner shown when the Rovo file upload rejects the file type (HTTP 415)',
});

const uploadErrorRaiPolicyMessage = defineMessage({
	id: 'navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal.upload-error-rai-policy',
	defaultMessage: 'The file might not comply with our Acceptable Use Policy. Try a different file.',
	description:
		'Error banner shown when the Rovo file upload is rejected by the Responsible AI content-safety check (HTTP 406)',
});

const IMPORT_CHOICE_MODAL_TEST_ID =
	'sidebar-nav4-sidebars-content-projects-import-work.import-choice-modal';

/**
 * How the user left the modal without picking an import option. Sent as the
 * `dismissMethod` attribute on `importChoiceModal dismissed` so abandonment can be
 * split by intent — an explicit Cancel reads very differently to an accidental
 * click on the blanket.
 */
type DismissMethod = 'cancelButton' | 'closeButton' | 'escapeKey' | 'outsideClick';

/**
 * The modal reports Escape and blanket-click dismissals through the same `onClose`
 * callback, distinguished only by the event it passes: a KeyboardEvent for Escape
 * and a MouseEvent for the blanket. Both the top-layer and legacy modal
 * implementations follow this contract (the top-layer one synthesises the event via
 * `createCloseEvent`), so keying off `key` works on either path.
 */
const isEscapeKeyEvent = (event: KeyboardOrMouseEvent): boolean =>
	'key' in event && event.key === 'Escape';

/**
 * Why a file failed to reach Rovo. Sent as the `failureReason` attribute on
 * `importChoiceModalCreateFromFiles failed`. The raw `statusCode` is sent too, but
 * on its own it can't distinguish "the service rejected this file" from "the
 * request never completed" — both of which need very different responses from us.
 */
type UploadFailureReason =
	| 'unsupportedFileType'
	| 'raiPolicyViolation'
	| 'serverError'
	| 'networkOrClientError';

/**
 * Single source of truth for how an upload failure is both surfaced to the user
 * and recorded in analytics, so the banner someone sees and the `failureReason` we
 * chart can't drift apart as new status codes are handled.
 */
const getUploadFailure = (
	statusCode: number | undefined,
): { failureReason: UploadFailureReason; message: typeof uploadErrorGenericMessage } => {
	switch (statusCode) {
		case UNSUPPORTED_FILE_TYPE_STATUS:
			return {
				failureReason: 'unsupportedFileType',
				message: uploadErrorUnsupportedTypeMessage,
			};
		case FILE_RAI_POLICY_VIOLATION_STATUS:
			return { failureReason: 'raiPolicyViolation', message: uploadErrorRaiPolicyMessage };
		default:
			return {
				// No status code means we never got a response — a dropped connection,
				// an aborted request, or a client-side throw. That's an availability
				// problem on our side, not the service refusing the file, so it must
				// not be lumped in with genuine server rejections.
				failureReason: statusCode == null ? 'networkOrClientError' : 'serverError',
				message: uploadErrorGenericMessage,
			};
	}
};

type Props = {
	isOpen: boolean;
	onClose: () => void;
};

export const ImportChoiceModal = ({ isOpen, onClose }: Props): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const fileInputRef = useRef<HTMLInputElement>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const publish = usePublish('ai-mate');
	const [isUploading, setIsUploading] = useState(false);
	const [uploadErrorMessage, setUploadErrorMessage] = useState<
		typeof uploadErrorGenericMessage | null
	>(null);

	useEffect(() => {
		if (!isOpen) {
			return;
		}
		// Denominator for every other modal event in the funnel (option selection
		// rate, abandonment rate). Keyed on `isOpen` rather than mount because this
		// component stays mounted across open/close cycles — see handleClose.
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'viewed',
				actionSubject: 'importChoiceModal',
			}),
		);
	}, [isOpen, createAnalyticsEvent]);

	const handleClose = useCallback(() => {
		// This component stays mounted between opens (only the inner Modal
		// unmounts), so transient state would otherwise survive a close/reopen
		// cycle — leaving a stale error banner, or a stale spinner if the modal
		// was dismissed mid-upload. Reset both here so every reopen starts clean.
		// All close paths route through this handler to keep that guarantee.
		setUploadErrorMessage(null);
		setIsUploading(false);
		onClose();
	}, [onClose]);

	// Deliberately separate from handleClose: a close that follows an option
	// selection is a conversion, not an abandonment. Only the paths that leave the
	// modal without choosing anything fire `dismissed`, so the abandonment metric
	// stays meaningful.
	const handleDismiss = useCallback(
		(dismissMethod: DismissMethod) => {
			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'dismissed',
					actionSubject: 'importChoiceModal',
					attributes: { dismissMethod },
				}),
			);
			handleClose();
		},
		[createAnalyticsEvent, handleClose],
	);

	const handleCancelClick = useCallback(() => {
		handleDismiss('cancelButton');
	}, [handleDismiss]);

	const handleCloseButtonClick = useCallback(() => {
		handleDismiss('closeButton');
	}, [handleDismiss]);

	// The modal's own onClose only fires for the two dismissals it owns: Escape and
	// a click on the blanket outside the dialog. The Cancel and X buttons are wired
	// to their own handlers above, so they never reach here.
	const handleModalDismiss = useCallback(
		(event: KeyboardOrMouseEvent) => {
			handleDismiss(isEscapeKeyEvent(event) ? 'escapeKey' : 'outsideClick');
		},
		[handleDismiss],
	);

	const handleCreateFromFilesClick = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'importChoiceModalCreateFromFiles',
			}),
		);
		setUploadErrorMessage(null);
		fileInputRef.current?.click();
	}, [createAnalyticsEvent]);

	const handleFileSelected = useCallback(
		async (event: React.ChangeEvent<HTMLInputElement>) => {
			const file = event.target.files?.[0];
			if (file == null) {
				return;
			}

			setIsUploading(true);
			setUploadErrorMessage(null);

			try {
				const uploadedFiles = await uploadFileToRovoAssistanceService(file);
				const uploadedFile = uploadedFiles[0];

				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'uploaded',
						actionSubject: 'importChoiceModalCreateFromFiles',
					}),
				);

				// Open the Rovo chat sidebar with the uploaded file attached and a
				// guided prompt, so the user lands directly on a pre-filled request
				// rather than an empty chat. `data.files` (not `attachments`) is the
				// contract the ai-mate chat surface expects — matches the existing
				// working implementation in ImportWorkCardRovo.tsx.
				publish({
					type: 'chat-new',
					product: 'jira',
					openChatMode: 'mini-modal',
					source: 'jira-navigation-apps-import-work-create-from-files',
					data: {
						dialogues: [],
						prompt: ROVO_IMPORT_PROMPT,
						files: uploadedFile
							? [
									{
										id: uploadedFile.id,
										name: uploadedFile.name,
										size: uploadedFile.size,
										type: uploadedFile.type,
										isLoading: false,
										fileObject: file,
									},
								]
							: [],
					},
				});

				// Bottom of the funnel we own: the file is uploaded and the chat request
				// has been handed off to Rovo. Everything past this point is emitted by
				// the Rovo platform itself (`rovoChatContext added file`,
				// `aiInteraction initiated`, `button clicked createWorkItemsBulkCreate`),
				// so this event is what those platform events get joined back to.
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'triggered',
						actionSubject: 'rovoFileUploadTask',
						// MIME type only — the file name is user-generated content and must
						// not be sent to analytics.
						attributes: { fileType: file.type },
					}),
				);

				handleClose();
			} catch (err) {
				const statusCode = err instanceof RovoFileUploadError ? err.statusCode : undefined;
				const { failureReason, message } = getUploadFailure(statusCode);

				// The counterpart to `rovoFileUploadTask triggered` — together they give
				// the success rate of the Rovo handoff, and `failureReason` says whether
				// a drop-off is us (availability) or the file (rejected content/type).
				// This is the product/funnel view of the failure.
				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'failed',
						actionSubject: 'importChoiceModalCreateFromFiles',
						// MIME type only — the file name is user-generated content and must
						// not be sent to analytics.
						attributes: { statusCode, failureReason, fileType: file.type },
					}),
				);

				// ...and this is the reliability view. fireErrorAnalytics emits an
				// operational event, so it lands in Splunk and the SignalFX reliability
				// dashboards where the UI event above is not visible. Both are needed:
				// one answers "is this experiment converting?", the other "is this
				// feature healthy in production right now?".
				fireErrorAnalytics({
					meta: {
						id: 'uploadFileToRovoAssistanceService',
						packageName: 'jiraNavigationAppsSidebarNav4SidebarsContentProjectsImportWork',
						teamName: 'growth-jira-find',
					},
					attributes: { failureReason, statusCode },
					error: err instanceof Error ? err : new Error(String(err)),
					// A status code means the service saw the request and answered: a
					// rejected file (415/406) is working as designed and a 5xx is already
					// alerted on server-side, so neither belongs in Sentry. No status code
					// means the request never completed, which can be a real client-side
					// bug and is the one case worth capturing.
					//
					// Note this is a top-level field, not an attribute — passing it inside
					// `attributes` (as the handling-errors skill example does) is silently
					// ignored and everything still reaches Sentry.
					skipSentry: statusCode != null,
				});

				setUploadErrorMessage(message);
			} finally {
				setIsUploading(false);
				// Reset so selecting the same file again still fires a change event.
				if (fileInputRef.current != null) {
					fileInputRef.current.value = '';
				}
			}
		},
		[createAnalyticsEvent, handleClose, publish],
	);

	return (
		<ProjectCreateDrawerConsumer>
			{({ methods: { open } }) => {
				const handleBringProjectClick = () => {
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'clicked',
							actionSubject: 'importChoiceModalBringYourProject',
						}),
					);
					// Open the drawer BEFORE closing our own modal. Closing first would
					// unmount this modal (and the ProjectCreateDrawerConsumer subtree)
					// in the same tick the drawer is asked to open, which can leave the
					// X2C wizard stuck in a loading state. This mirrors the previously
					// working direct-open implementation.
					open({ isImportProjectOpenInitially: true });
					handleClose();
				};

				return (
					<ModalTransition>
						{isOpen && (
							<Modal
								messageId="sidebar-nav4-sidebars-content-projects-import-work.modal.navigation-apps-sidebar-nav4-sidebars-content-projects-import-work.choice-modal"
								messageType="transactional"
								shouldCloseOnEscapePress
								onClose={handleModalDismiss}
								width={600}
								testId={IMPORT_CHOICE_MODAL_TEST_ID}
							>
								{/*
									@atlaskit/modal-dialog's internal Positioner div is a
									flex-direction:column container that spans nearly the full
									viewport height. Its content (the auto-sized dialog card) uses
									the flex default `justify-content: flex-start`, so the card sits
									flush at the *top* of that box rather than being vertically
									centered (see platform/packages/design-system/modal-dialog/.../
									positioner.tsx, ref AFBH-1489). This <style> block scopes a
									`justify-content: center` override to just this modal instance's
									positioner (via its unique testId + `--positioner` suffix) to
									vertically center the card, without touching shared
									design-system styles used by every other modal.
								*/}
								{/* eslint-disable-next-line @atlaskit/ui-styling-standard/no-global-styles -- Scoped via a unique per-instance testId selector (not truly global); no xcss/cssMap equivalent exists for overriding another element's flex alignment or applying a positional transform. */}
								<style>{`
									[data-testid="${IMPORT_CHOICE_MODAL_TEST_ID}--positioner"][data-testid="${IMPORT_CHOICE_MODAL_TEST_ID}--positioner"] {
										justify-content: center;
									}
									/*
										The design spec's 322px is a *resting* height, not a fixed
										one — using Modal's \`height\` prop would lock it, forcing the
										body to scroll when the conditional upload-error banner
										appears. min-height keeps the spec size when there's no
										banner, while still letting the dialog grow to fit it.

										The transform nudges the card 11px up from its centered
										resting position; transform doesn't affect layout (unlike
										inset/top properties), so it's a pure visual offset that
										doesn't disturb the centering above.
									*/
									[data-testid="${IMPORT_CHOICE_MODAL_TEST_ID}"][data-testid="${IMPORT_CHOICE_MODAL_TEST_ID}"] {
										min-height: 322px;
										transform: translateY(-11px);
									}
								`}</style>
								<ModalHeader>
									<Box xcss={headerContentStyles}>
										<ModalTitle>{formatMessage(titleMessage)}</ModalTitle>
										<CloseButton
											onClick={handleCloseButtonClick}
											label={formatMessage(closeButtonLabelMessage)}
										/>
									</Box>
								</ModalHeader>

								<ModalBody>
									{uploadErrorMessage != null && (
										<Box xcss={errorBannerWrapperStyles}>
											<SectionMessage
												appearance="error"
												testId="sidebar-nav4-sidebars-content-projects-import-work.upload-error"
											>
												{formatMessage(uploadErrorMessage)}
											</SectionMessage>
										</Box>
									)}
									{/* While the file uploads, the two cards and the MCP footer are hidden
									    with `visibility: hidden` rather than unmounted, and the spinner is
									    overlaid on top of them. Keeping them mounted means the modal holds
									    the exact height it had before the upload started, so nothing
									    resizes — without hard-coding a height that would drift the moment
									    the copy changes. `visibility` (unlike `opacity`) also takes the
									    hidden content out of the tab order and the accessibility tree. */}
									<Box xcss={optionsAreaStyles}>
										<Box xcss={isUploading ? contentHiddenStyles : undefined}>
											<Flex gap="space.200">
												{/* Create from files card */}
												<Pressable
													xcss={optionCardStyles}
													onClick={handleCreateFromFilesClick}
													isDisabled={isUploading}
													testId="sidebar-nav4-sidebars-content-projects-import-work.create-from-files-button"
												>
													<Box xcss={optionIconStyles}>
														<CreateFromFilesIcon />
													</Box>
													<Box xcss={optionTextStyles}>
														<Text weight="bold" size="medium" as="p">
															{formatMessage(createFromFilesOptionMessage)}
														</Text>
														<Text as="p" color="color.text.subtle" size="small">
															{formatMessage(createFromFilesDescriptionMessage)}
														</Text>
													</Box>
												</Pressable>

												{/* Bring your project card */}
												<Pressable
													xcss={optionCardStyles}
													onClick={handleBringProjectClick}
													testId="sidebar-nav4-sidebars-content-projects-import-work.bring-project-button"
												>
													<Box xcss={optionIconStyles}>
														<BringYourProjectIcon />
													</Box>
													<Box xcss={optionTextStyles}>
														<Text weight="bold" size="medium" as="p">
															{formatMessage(bringYourProjectOptionMessage)}
														</Text>
														<Text as="p" color="color.text.subtle" size="small">
															{formatMessage(bringYourProjectDescriptionMessage)}
														</Text>
													</Box>
												</Pressable>
											</Flex>

											<Box xcss={mcpFooterTextStyles}>
												<Text size="small" color="color.text.subtle">
													{formatMessage(mcpFooterTextMessage)}{' '}
													{formatMessage(mcpFooterLinkMessage, {
														link: (chunks: ReactNode) => (
															<Anchor
																href="https://developer.atlassian.com/cloud/jira/platform/mcp/"
																target="_blank"
															>
																{chunks}
															</Anchor>
														),
													})}
												</Text>
											</Box>
										</Box>

										{isUploading && (
											<Box
												xcss={uploadingOverlayStyles}
												role="status"
												testId="sidebar-nav4-sidebars-content-projects-import-work.uploading-state"
											>
												<Spinner size="large" />
												<Text color="color.text.subtle">
													{formatMessage(uploadingLabelMessage)}
												</Text>
											</Box>
										)}
									</Box>

									{/* Hidden file input, opened via the "Create from files" card */}
									<Box xcss={hiddenInputStyles}>
										<input
											ref={fileInputRef}
											type="file"
											// Intentionally no `accept` restriction, matching the real Rovo
											// chat's own file input (see useConversationFiles.tsx): the
											// assistance service supports a broad range of file types and
											// validates server-side, so we let the user pick anything and
											// surface a precise error if the service rejects it (see
											// UNSUPPORTED_FILE_TYPE_STATUS / FILE_RAI_POLICY_VIOLATION_STATUS
											// handling in handleFileSelected).
											disabled={isUploading}
											onChange={handleFileSelected}
											data-testid="sidebar-nav4-sidebars-content-projects-import-work.file-input"
										/>
									</Box>
								</ModalBody>

								<ModalFooter>
									<Box xcss={footerButtonWrapperStyles}>
										<Button appearance="subtle" onClick={handleCancelClick}>
											{formatMessage(cancelMessage)}
										</Button>
									</Box>
								</ModalFooter>
							</Modal>
						)}
					</ModalTransition>
				);
			}}
		</ProjectCreateDrawerConsumer>
	);
};

// SectionMessage (appearance="error") already provides the background color,
// icon, and internal padding matching the design spec — this wrapper only
// adds the bottom margin needed to separate it from the option cards below.
const errorBannerWrapperStyles = xcss({
	marginBlockEnd: 'space.150',
});

// Layout-only styles for the content nested inside ModalHeader/ModalFooter.
// No padding here — ModalHeader/ModalFooter already apply the design system's
// standard padding, so adding our own would double it up.
const headerContentStyles = xcss({
	display: 'flex',
	alignItems: 'flex-start',
	justifyContent: 'space-between',
	gap: 'space.200',
	width: '100%',
});

// ModalFooter's own default styles (see modal-footer.tsx) already provide
// justifyContent: 'flex-end', paddingInline: 'space.300', and
// paddingBlockStart: 'space.200' — matching the requested 300 side / 200
// top-bottom footer spacing. Its paddingBlockEnd defaults to 'space.300'
// though, so this negative margin trims that down to the requested
// symmetric 'space.200' top/bottom padding without needing to override
// ModalFooter's internal styles directly (no style-override prop is exposed).
const footerButtonWrapperStyles = xcss({
	marginBlockEnd: 'space.negative.100',
});

// Below both option cards, with 'space.200' top spacing separating it from
// the cards above.
const mcpFooterTextStyles = xcss({
	marginBlockStart: 'space.200',
});

const optionCardStyles = xcss({
	flex: '1',
	padding: 'space.200',
	borderRadius: 'radius.large',
	borderWidth: 'border.width',
	borderStyle: 'solid',
	borderColor: 'color.border',
	cursor: 'pointer',
	backgroundColor: 'color.background.neutral.subtle',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	gap: 'space.150',
	':hover': {
		backgroundColor: 'color.background.neutral.subtle.hovered',
	},
});

// Positioning context for the upload spinner, which is overlaid on top of the
// (hidden) cards rather than replacing them, so the modal keeps its height.
const optionsAreaStyles = xcss({
	position: 'relative',
});

const contentHiddenStyles = xcss({
	visibility: 'hidden',
});

const uploadingOverlayStyles = xcss({
	position: 'absolute',
	inset: '0',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: 'space.150',
});

const optionIconStyles = xcss({
	display: 'flex',
	// Keep the 32x32 icon from being compressed by the flex row when space is tight.
	flexShrink: 0,
});

const optionTextStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'flex-start',
	textAlign: 'left',
	gap: 'space.100',
});

const hiddenInputStyles = xcss({
	display: 'none',
});
